import React, { useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import { removeTypename } from 'shared/helpers/utils';
import { SmallLoader } from 'shared/components';

import { useDposLoyalty } from 'src/components/embedded-account/dpos-loyalty-context';
import { useGetRewards } from './use-get-rewards';
import { detectLoyaltyIntegration, useLoyalty, Statuses, Integrations } from './use-loyalty';
import { Connect } from './connect';
import { Terms } from './terms';
import { Rewards, DposRewards } from './rewards';
import { Pin } from './pin';
import { useLoyalty as useLoyaltyDpos } from '../widgets/loyalty-tiers/use-loyalty';

export const Loyalty = (): JSX.Element => {
  const flags = useFlags();
  const isLegacyWidgetBalanceFixEnabled = flags['pos.backoffice.legacy-widget-balance-fix.rollout'] ?? false;

  const { dispensary } = useDispensary();
  const loyaltyProgram = detectLoyaltyIntegration(dispensary);
  const { status, setStatus, programName, programDescription, createUserAndPin, processPin, resendPin } = useLoyalty({
    initialStatus: Statuses.loading,
    integration: loyaltyProgram,
    surface: 'accountPanel',
  });
  const { currentBalance: dposCurrentBalance = 0 } = useLoyaltyDpos();
  const User = useUser();
  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);

  const pin = User.getLoyaltyPin(dispensary.id);
  const customerId = User?.id;
  const isSpringbig = loyaltyProgram === Integrations.springbig;
  const isAlpineiq = loyaltyProgram === Integrations.alpineiq;
  const isDutchiepos = loyaltyProgram === Integrations.dutchiepos;

  // Dutchiepos data comes from the shared context (avoids duplicate fetches with ReferralsPanel/nav bar)
  const dposLoyalty = useDposLoyalty();

  // Rewards query for non-dutchiepos integrations only
  const nonDposSkip =
    !phone ||
    (isAlpineiq && !pin) ||
    ([Statuses.signup, Statuses.terms, Statuses.pin].includes(status) && !isSpringbig);

  const { data, loading } = useGetRewards({
    dispensaryId: dispensary.id,
    birthday,
    pin,
    phone,
    skip: isDutchiepos || nonDposSkip,
    customerId,
  });

  const {
    rewards: nonDposRawRewards = [],
    balance = isDutchiepos && isLegacyWidgetBalanceFixEnabled ? dposCurrentBalance.toString() : '0',
    userHasWallet = false,
    isLoyaltyMember: nonDposIsLoyaltyMember = false,
    rewardsActivity: nonDposRawRewardsActivity = [],
  } = data?.getRewardsV2 ?? {};

  const effectiveLoading = isDutchiepos ? dposLoyalty.loading : loading;

  useEffect(() => {
    if (!effectiveLoading) {
      if (isDutchiepos) {
        setStatus(!dposLoyalty.isLoyaltyMember && dposLoyalty.loyaltyRequireOptIn ? Statuses.signup : Statuses.success);
      } else {
        const isPinConfirmed = data?.getRewardsV2?.auth?.pinConfirmed;
        setStatus(
          !data || (isAlpineiq && !isPinConfirmed) || (isSpringbig && !userHasWallet)
            ? Statuses.signup
            : Statuses.success
        );
      }
    }
  }, [
    isDutchiepos,
    effectiveLoading,
    dposLoyalty.isLoyaltyMember,
    dposLoyalty.loyaltyRequireOptIn,
    data,
    isAlpineiq,
    isSpringbig,
    userHasWallet,
    setStatus,
  ]);

  const filteredRewards = isDutchiepos
    ? dposLoyalty.rewards
    : nonDposRawRewards.filter((r) => r !== null).map((r) => removeTypename(r));

  const filteredRewardsActivity = isDutchiepos
    ? dposLoyalty.rewardsActivity
    : nonDposRawRewardsActivity?.filter((ra) => ra !== null).map((ra) => removeTypename(ra));

  return (
    <>
      {status === Statuses.loading && <SmallLoader data-testid='small-loader' />}

      {status === Statuses.signup && (
        <Connect setStatus={setStatus} programName={programName} programDescription={programDescription} />
      )}

      {[Statuses.terms, Statuses.verificationRequired].includes(status) && (
        <Terms setStatus={setStatus} createUserAndPin={createUserAndPin} programName={programName} />
      )}

      {[Statuses.pin, Statuses.incorrectPin].includes(status) && (
        <Pin status={status} programName={programName} processPin={processPin} resendPin={resendPin} />
      )}

      {status === Statuses.success && !isDutchiepos && (
        <Rewards rewards={filteredRewards} balance={balance} programName={programName} />
      )}

      {status === Statuses.success && isDutchiepos && (
        <DposRewards
          referral={dposLoyalty.referral ?? null}
          rewards={filteredRewards}
          rewardsActivity={filteredRewardsActivity}
          dposConfigData={dposLoyalty.dposConfigData}
          isRevokedFromReferralProgram={dposLoyalty.isRevokedFromReferralProgram}
        />
      )}
    </>
  );
};
